// pages/module-pages/reviews/ReviewsPage.tsx
import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Filter, X, Eye, EyeOff } from "lucide-react";
import PageLayout from "../../../layouts/PageLayout";
import DeleteConfirmationModal from "../../../components/ui/DeleteConfirmationModal";
import MenuModal, { Menu } from "../../../components/ui/MenuModal";
import useReviews from "../../../hooks/features/reviews/useReviews";
import { Review } from "../../../types/features/Review";
import { alertSuccess, alertError } from "../../../helpers/alertError";
import ReviewsList from "../../../components/shared/reviews/ReviewsList";
import PageLitLayout from "../../../layouts/PageListLayout";
import { useProduct } from "../../../hooks/core/useProduct";

export default function ReviewsPage() {
  const navigate = useNavigate();
  const { selectedProduct } = useProduct();
  const { reviews, deleteReview, deleteManyReviews, toggleVisibility, loading } = useReviews();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPlatform, setSelectedPlatform] = useState<string>("");
  const [selectedRating, setSelectedRating] = useState<string>("");
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const [reviewToDelete, setReviewToDelete] = useState<Review | null>(null);
  const [itemsToDelete, setItemsToDelete] = useState<string[] | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // ✅ Filtrer les avis par produit
  const reviewsByProduct = useMemo(() => {
    if (selectedProduct && selectedProduct !== 'all') {
      return reviews.filter((r) => r.product_name === selectedProduct);
    }
    return reviews;
  }, [reviews, selectedProduct]);

  // ✅ Stats basées sur les avis filtrés par produit
  const stats = {
    total: reviewsByProduct.length,
    playStore: reviewsByProduct.filter((r) => r.platform === "play_store").length,
    appStore: reviewsByProduct.filter((r) => r.platform === "app_store").length,
    visible: reviewsByProduct.filter((r) => r.is_visible).length,
    averageRating:
      reviewsByProduct.length > 0 
        ? (reviewsByProduct.reduce((acc, r) => acc + r.rating, 0) / reviewsByProduct.length).toFixed(1) 
        : "0",
  };

  // ✅ Filtrer les avis (recherche + plateforme + note + statut) à partir des avis déjà filtrés par produit
  const filteredReviews = useMemo(() => {
    let result = reviewsByProduct;

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(
        (r) => r.user_name.toLowerCase().includes(searchLower) || r.comment.toLowerCase().includes(searchLower)
      );
    }

    if (selectedPlatform) {
      result = result.filter((r) => r.platform === selectedPlatform);
    }

    if (selectedRating) {
      result = result.filter((r) => r.rating === parseInt(selectedRating));
    }

    if (selectedStatus) {
      const isVisible = selectedStatus === "visible";
      result = result.filter((r) => r.is_visible === isVisible);
    }

    return result;
  }, [reviewsByProduct, searchTerm, selectedPlatform, selectedRating, selectedStatus]);

  const handleToggleVisibility = async (review: Review) => {
    const success = await toggleVisibility(review.id, review.is_visible);
    if (success) {
      alertSuccess(review.is_visible ? "Avis masqué" : "Avis affiché");
    } else {
      alertError("Erreur lors du changement de statut");
    }
  };

  const handleDelete = async () => {
    if (!reviewToDelete) return;
    setIsDeleting(true);
    const success = await deleteReview(reviewToDelete.id);
    setIsDeleting(false);
    if (success) {
      setReviewToDelete(null);
      alertSuccess("Avis supprimé avec succès");
    } else {
      alertError("Erreur lors de la suppression");
    }
  };

  const handleDeleteMany = async () => {
    if (!itemsToDelete || itemsToDelete.length === 0) return;
    const success = await deleteManyReviews(itemsToDelete);
    if (success) {
      setItemsToDelete(null);
      alertSuccess(`${itemsToDelete.length} avis supprimés`);
    } else {
      alertError("Erreur lors de la suppression multiple");
    }
  };

  const clearFilters = () => {
    setSelectedPlatform("");
    setSelectedRating("");
    setSelectedStatus("");
    setSearchTerm("");
  };

  const hasActiveFilters = selectedPlatform || selectedRating || selectedStatus || searchTerm;

  const getActionsMenu = (review: Review): Menu[] => {
    const actions: Menu[] = [
      {
        label: review.is_visible ? "Masquer" : "Afficher",
        icon: review.is_visible ? EyeOff : Eye,
        onClick: () => handleToggleVisibility(review),
      },
      {
        label: "Modifier",
        icon: Plus,
        onClick: () => navigate(`/admin/reviews/update?id=${review.id}`),
      },
      {
        label: "Supprimer",
        icon: Plus,
        onClick: () => setReviewToDelete(review),
      },
    ];
    return actions;
  };

  return (
    <PageLitLayout title="Gestion des avis">
      <div className="px-6 py-4 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Avis</h1>
              <p className="text-sm text-gray-500 mt-1">Gérez les avis Play Store et App Store</p>
            </div>
            <button
              onClick={() => navigate("/admin/reviews/new")}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded-xl hover:bg-primary/90"
            >
              <Plus size={18} />
              Nouvel avis
            </button>
          </div>

          {/* Indicateur de filtre produit */}
          {selectedProduct && selectedProduct !== 'all' && (
            <div className="mt-4 p-3 bg-blue-50 rounded-xl border border-blue-200 text-sm text-blue-700">
              Filtré par produit : <span className="font-semibold">{selectedProduct}</span>
            </div>
          )}

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mt-6">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4">
              <p className="text-xs text-blue-700/70">Total</p>
              <p className="text-2xl font-bold text-blue-700">{stats.total}</p>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4">
              <p className="text-xs text-green-700/70">Play Store</p>
              <p className="text-2xl font-bold text-green-700">{stats.playStore}</p>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4">
              <p className="text-xs text-purple-700/70">App Store</p>
              <p className="text-2xl font-bold text-purple-700">{stats.appStore}</p>
            </div>
            <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-4">
              <p className="text-xs text-emerald-700/70">Visibles</p>
              <p className="text-2xl font-bold text-emerald-700">{stats.visible}</p>
            </div>
            <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-4">
              <p className="text-xs text-yellow-700/70">Note moyenne</p>
              <p className="text-2xl font-bold text-yellow-700">{stats.averageRating}</p>
            </div>
          </div>

          {/* Recherche */}
          <div className="relative mt-6">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher un avis..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/50"
            />
          </div>

          {/* Filtres */}
          <div className="mt-4">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${showFilters ? "bg-primary text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
            >
              <Filter size={18} /> Filtrer
              {hasActiveFilters && <span className="w-2 h-2 bg-red-500 rounded-full" />}
            </button>

            {showFilters && (
              <div className="bg-gray-50 rounded-xl p-4 mt-3 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-gray-700">Filtres</h3>
                  {hasActiveFilters && (
                    <button
                      onClick={clearFilters}
                      className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1"
                    >
                      <X size={14} /> Effacer tout
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Plateforme</label>
                    <select
                      value={selectedPlatform}
                      onChange={(e) => setSelectedPlatform(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="">Toutes</option>
                      <option value="play_store">Play Store</option>
                      <option value="app_store">App Store</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
                    <select
                      value={selectedRating}
                      onChange={(e) => setSelectedRating(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="">Toutes</option>
                      <option value="5">5 étoiles</option>
                      <option value="4">4 étoiles</option>
                      <option value="3">3 étoiles</option>
                      <option value="2">2 étoiles</option>
                      <option value="1">1 étoile</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                    <select
                      value={selectedStatus}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="">Tous</option>
                      <option value="visible">Visible</option>
                      <option value="hidden">Masqué</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="px-6 py-8 max-w-7xl mx-auto">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <ReviewsList
            reviews={filteredReviews}
            onAction={(review) => setSelectedReview(review)}
            onSelectReviews={(selected) => setItemsToDelete(selected.map((r) => r.id))}
            selectable={true}
            selectActions={[
              {
                label: "Supprimer",
                onClick: (selected) => setItemsToDelete(selected.map((r) => r.id)),
                className: "bg-red-600 text-white",
              },
            ]}
          />
        )}
      </div>

      {/* Menu modal */}
      {selectedReview && (
        <MenuModal
          title={selectedReview.user_name}
          isOpen={!!selectedReview}
          onClose={() => setSelectedReview(null)}
          icon={null}
          menu={getActionsMenu(selectedReview)}
        />
      )}

      {/* Modals suppression */}
      <DeleteConfirmationModal
        isOpen={!!reviewToDelete}
        onClose={() => setReviewToDelete(null)}
        onConfirm={handleDelete}
        title="Supprimer l'avis"
        message={`Supprimer l'avis de "${reviewToDelete?.user_name}" ?`}
        confirmText={isDeleting ? "Suppression..." : "Supprimer"}
      />

      <DeleteConfirmationModal
        isOpen={!!itemsToDelete && itemsToDelete.length > 0}
        onClose={() => setItemsToDelete(null)}
        onConfirm={handleDeleteMany}
        title="Suppression multiple"
        message={`Supprimer ${itemsToDelete?.length} avis ?`}
      />
    </PageLitLayout>
  );
}